import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { Coordinates } from '../Types/DraftOperation';
import Location from '../Types/Location';
import APIService from '../Utils/APIService';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';
import { RootState } from './store';
import { getUserType } from './auth.store';

export interface LocationState {
  loading: boolean;
  locations: Location[];
  minimalLocations: any[];
  editingLocation: Location | null;
  locationCoords: Coordinates | null;
  pageStart: number;
}

const initialState = {
  loading: false,
  locations: [],
  editingLocation: null,
  locationCoords: null,
  pageStart: 0,
  minimalLocations: [],
} as LocationState;

export const addLocation = createAsyncThunk<
  any,
  {
    locationName: string;
    address: string;
    longitude: string;
    latitude: string;
    status: string;
    waiting_time: number;
    contact_no: string;
  }
>('locations/add', async (payload, { rejectWithValue, dispatch }) => {
  const [err, res]: any = await to(
    APIService.post('user/saved-locations', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  );
  if (err) {
    toast.error('Something went wrong with data. Please try again!');
    return rejectWithValue(err);
  }
  dispatch(
    fetchLocations({
      start: 0,
      limit: DEFAULT_PAGE_SIZE,
      locationName: '',
      status: 'ACTIVE',
    }),
  );
  toast.success('Successfully Added the Location');
  return res;
});

export const updateLocation = createAsyncThunk<
  any,
  {
    locationId?: string;
    locationName: string;
    address: string;
    longitude: string;
    latitude: string;
    status: string;
    waiting_time: number;
    contact_no: string;
  }
>('locations/update', async (payload, { rejectWithValue, dispatch }) => {
  const { locationId, ...body } = payload;
  const [err, res]: any = await to(
    APIService.put(`user/saved-locations/${locationId}`, body, {
      headers: { 'Content-Type': 'application/json' },
    }),
  );
  if (err) {
    toast.error('Something went wrong with data. Please try again!');
    return rejectWithValue(err);
  }
  dispatch(
    fetchLocations({
      start: 0,
      limit: DEFAULT_PAGE_SIZE,
      locationName: '',
      status: 'ACTIVE',
    }),
  );
  toast.success('Successfully Updated the Location');
  return res;
});

export const fetchLocations = createAsyncThunk<
  any,
  { start: number; limit: number; locationName: string; status: 'ACTIVE' | 'INACTIVE' }
>('locations/get', async ({ start, limit, locationName, status }, { rejectWithValue }) => {
  const page = Math.floor(start / limit) + 1;
  const [err, data] = await to(
    APIService.get('user/saved-locations', {
      params: { page, page_size: limit, locationName, status },
    }),
  );
  if (err) {
    toast.error('Locations not loaded. Please try again!');
    rejectWithValue(err);
  }
  const body = data?.data;
  return Array.isArray(body) ? body : body?.data ?? [];
});

export const fetchMinimalLocations = createAsyncThunk<any, any>(
  'locations/minimal/get',
  async (_, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    if (userType === 'admin') {
      return [];
    }

    const [firstErr, firstData] = await to(
      APIService.get('user/saved-locations', { params: { page: 1, page_size: 100 } }),
    );
    if (firstErr) {
      toast.error('Location not loaded');
      return rejectWithValue(firstErr);
    }

    const firstBody = firstData?.data;
    const firstPageLocations = Array.isArray(firstBody?.data) ? firstBody.data : [];
    const totalPages = firstBody?.total_pages ?? 1;
    const remainingPageNumbers = Array.from({ length: totalPages - 1 }, (__, i) => i + 2);
    const remainingResults = await Promise.all(
      remainingPageNumbers.map((page) =>
        to(APIService.get('user/saved-locations', { params: { page, page_size: 100 } })),
      ),
    );

    const remainingLocations = remainingResults.flatMap(([fetchErr, data]) => {
      if (fetchErr) {
        return [];
      }
      const body = data?.data;
      return Array.isArray(body?.data) ? body.data : [];
    });
    const allLocations = [...firstPageLocations, ...remainingLocations];
    return allLocations.filter((loc) => loc.status === 'ACTIVE');
  },
);

export const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setEditingLocation: (state, { payload }) => {
      state.editingLocation = payload;
    },
    setLocationCoords: (state, { payload }) => {
      state.locationCoords = payload;
    },
    setPageStart: (state, { payload }) => {
      state.pageStart = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLocations.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(fetchLocations.rejected, (store) => {
      store.loading = false;
    });
    builder.addCase(fetchLocations.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.locations = payload;
    });
    builder.addCase(fetchMinimalLocations.fulfilled, (state, { payload }) => {
      state.minimalLocations = payload;
    });
  },
});

export const { setEditingLocation, setLocationCoords, setPageStart } = locationSlice.actions;

export const locationStore = (store: RootState): LocationState => store.location;

export const getLocations = createSelector(locationStore, (location) => location.locations);
export const getLocationsLoading = createSelector(locationStore, (location) => location.loading);
export const getEditingLocation = createSelector(locationStore, (location) => location.editingLocation);
export const getLocationCoords = createSelector(locationStore, (location) => location.locationCoords);
export const getPageStart = createSelector(locationStore, (location) => location.pageStart);
export const getMinimalLocations = createSelector(locationStore, (location) =>
  location.minimalLocations.filter((l) => l.status === 'ACTIVE'),
);

export default locationSlice.reducer;
